'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

// Action phrases that cycle in the animated line
const PHRASES = [
  'achetez.',
  'louez.',
  'investissez.',
];

// Typing speeds (ms)
const TYPE_SPEED = 65;
const DELETE_SPEED = 32;
const PAUSE_AFTER_TYPE = 2000;
const PAUSE_AFTER_DELETE = 300;

interface HeroTitleProps {
  className?: string;
}

export function HeroTitle({ className }: HeroTitleProps) {
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [displayed, setDisplayed] = useState('');
  const [phase, setPhase] = useState<'typing' | 'pausing' | 'deleting' | 'waiting'>('typing');
  const [cursorOn, setCursorOn] = useState(true);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Cursor blink — always runs independently
  useEffect(() => {
    const id = setInterval(() => setCursorOn((v) => !v), 500);
    return () => clearInterval(id);
  }, []);

  // Typewriter state machine
  useEffect(() => {
    const target = PHRASES[phraseIdx];
    clearTimeout(timeoutRef.current);

    if (phase === 'typing') {
      if (displayed.length < target.length) {
        timeoutRef.current = setTimeout(() => {
          setDisplayed(target.slice(0, displayed.length + 1));
        }, TYPE_SPEED);
      } else {
        setPhase('pausing');
      }
    } else if (phase === 'pausing') {
      timeoutRef.current = setTimeout(() => setPhase('deleting'), PAUSE_AFTER_TYPE);
    } else if (phase === 'deleting') {
      if (displayed.length > 0) {
        timeoutRef.current = setTimeout(() => {
          setDisplayed((d) => d.slice(0, -1));
        }, DELETE_SPEED);
      } else {
        setPhase('waiting');
      }
    } else if (phase === 'waiting') {
      timeoutRef.current = setTimeout(() => {
        setPhraseIdx((i) => (i + 1) % PHRASES.length);
        setPhase('typing');
      }, PAUSE_AFTER_DELETE);
    }

    return () => clearTimeout(timeoutRef.current);
  }, [displayed, phase, phraseIdx]);

  return (
    <h1
      className={cn(
        'font-display text-display md:text-display-lg lg:text-display-xl',
        'text-primary-foreground leading-[1.02] tracking-tight',
        className,
      )}
    >
      L&rsquo;adresse qui vous ressemble —
      <br />
      <em className="not-italic text-accent-300" aria-live="polite">
        {displayed}
        <span
          aria-hidden
          className={cn(
            'inline-block w-[3px] h-[0.8em] ml-1 bg-accent-300 align-middle rounded-[1px] transition-opacity duration-100',
            cursorOn ? 'opacity-100' : 'opacity-0',
          )}
        />
      </em>
    </h1>
  );
}
